import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';

import { environment } from '../../src/environments/environment';

export class AppInsightsLogService {

    private applicationInsights: ApplicationInsights;
    private queue = [];

    initialize(config) {
        if (config.connectionString?.length) {
            try {
                this.applicationInsights = new ApplicationInsights({
                    config: {
                        connectionString: config.connectionString
                    }
                });
                this.applicationInsights.loadAppInsights();
                this.logQueueToAppinsights();
            } catch (e) {
                this.applicationInsights = undefined;
                if (!environment.production || window['__show_webagent_console_output__'] === true) {
                    console.error(`Azure Application Insights failed to load: ${e.message}`);
                }
            }
        }
    }

    setAuthenticatedUserContext(userId: string) {
        if (this.applicationInsights) {
            this.applicationInsights.setAuthenticatedUserContext(userId);
        }
    }

    clearAuthenticatedUserContext() {
        if (this.applicationInsights) {
            this.applicationInsights.clearAuthenticatedUserContext();
        }
    }

    registerAccount(accountId) {
        if (this.applicationInsights) {
            this.applicationInsights.config.accountId = accountId;
        }
    }

    trackException(error: Error, contextData?: any) {
        if (this.applicationInsights) {
            this.applicationInsights.trackException({
                error,
                properties: contextData
            });
        } else {
            this.queue.push([ 'trackException', { error } ]);
        }
    }

    trackEvent(name: string, contextData?: any) {
        const eventInfo = {
            name,
            properties: contextData
        };
        if (this.applicationInsights) {
            this.applicationInsights.trackEvent(eventInfo);
        } else {
            this.queue.push([ 'trackEvent', eventInfo ]);
        }
    }

    trackInfo(message: string, contextData?: any) {
        message = this.formatString(message, contextData);
        const logInfo = {
            message,
            severityLevel: SeverityLevel.Information,
            properties: contextData
        };

        if (!environment.production || window['__show_webagent_console_output__'] === true) {
            console.info(
                `%c${message}`,
                'color: green; font-weight: bold;',
                contextData && Object.keys(contextData).length ? contextData : undefined
            );
        }

        if (this.applicationInsights) {
            this.applicationInsights.trackTrace(logInfo);
        } else {
            this.queue.push([ 'trackTrace', logInfo ]);
        }
    }

    trackTrace(message: string, contextData?: any) {
        message = this.formatString(message, contextData);
        const logInfo = {
            message,
            severityLevel: SeverityLevel.Verbose,
            properties: contextData
        };

        if (this.applicationInsights) {
            this.applicationInsights.trackTrace(logInfo);
        } else {
            this.queue.push([ 'trackTrace', logInfo ]);
        }
    }

    trackError(message: string, contextData?: any) {
        message = this.formatString(message, contextData);
        if (!environment.production || window['__show_webagent_console_output__'] === true) {
            console.error(
                message,
                contextData && Object.keys(contextData).length ? contextData : undefined
            );
        }

        const logInfo = {
            message,
            severityLevel: SeverityLevel.Error,
            properties: contextData
        };

        if (this.applicationInsights) {
            this.applicationInsights.trackTrace(logInfo);
        } else {
            this.queue.push([ 'trackTrace', logInfo ]);
        }
    }

    unload() {
        if (this.applicationInsights) {
            this.applicationInsights.unload();
        }
    }

    private logQueueToAppinsights() {
        this.queue.forEach(([ name, ...parameters ]) => this.applicationInsights[name](...parameters));
        this.queue = [];
    }

    private formatString(target, params) {
        if (!params) {
            return target;
        }
        return target.replace(/(\{\w+\})/g, (matchingField: string) => {
            const fieldName = matchingField.substring(1, matchingField.length - 1);
            const value = params[fieldName];

            if (Array.isArray(value)) {
                return value.join(', ');
            }

            return value ?? '';
        });
    }
}
